import type { EccLogOnRequest, Patrol } from '@local/common-lib';
import * as Sentry from '@sentry/react';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCard } from '../../components/alert-card/index.js';
import { useLocalStorage } from '../../hooks/use-local-storage.js';
import { useVehicleRegister } from '../../hooks/use-vehicle-register.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import type { ExistingEntityViewProps } from '../../utils/types.js';
import { formikCheck } from '../../utils/validation.js';
import type { EccLogOnForm } from './ecc-log-on.view.js';
import {
  EccLogOnView,
  eccLogOnFormContract,
  eccLogOnFormContractNoVehicle,
} from './ecc-log-on.view.js';
import { ExistingPatrolPage } from './existing-patrol.js';
import type { PreviousVehicles } from './recent-vehicles.js';

export const EccLogOnContent: FC<ExistingEntityViewProps<Patrol.Type>> = ({
  data: patrol,
  id,
}) => {
  const auth = useAuthenticated();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isImmediate = searchParams.get('immediate') === 'true';
  const [previousVehicles, setPreviousVehicles] = useLocalStorage<
    Record<string, PreviousVehicles>
  >('vehicle-history', {});
  const { enqueueSnackbar } = useSnackbar();

  const recentVehicles = useMemo(() => {
    const values = Object.values(previousVehicles);
    values.sort((a, b) => b.lastUsed - a.lastUsed);
    return values.map((value) => value.vehicle).slice(0, 5);
  }, [previousVehicles]);

  const [combinedVehicles] = useVehicleRegister(recentVehicles);

  const eccLogOnForm = useFormik<EccLogOnForm>({
    initialValues: {
      hasPoliceRadio: false,
      vehicle: true,
      vehicleColor: '',
      vehicleHasLivery: false,
      vehicleMake: '',
      vehicleModel: '',
      vehicleRego: '',
    },
    onReset: () => {
      if (isImmediate) {
        navigate(`/patrol/${id}`);
      } else {
        navigate(-1);
      }
    },
    onSubmit: async (values) => {
      const body: EccLogOnRequest = {
        hasPoliceRadio: values.hasPoliceRadio,
        vehicle: values.vehicle
          ? {
              color: values.vehicleColor,
              hasLivery: values.vehicleHasLivery,
              make: values.vehicleMake,
              model: values.vehicleModel,
              rego: values.vehicleRego.toUpperCase(),
            }
          : undefined,
        when: new Date(),
      };
      try {
        if (body.vehicle) {
          setPreviousVehicles({
            ...recentVehicles
              .map((recent) => previousVehicles[recent.rego])
              .reduce<Record<string, PreviousVehicles>>(
                (acc, item) =>
                  item ? { ...acc, [item.vehicle.rego]: item } : acc,
                {},
              ),
            [body.vehicle.rego.toUpperCase()]: {
              lastUsed: Date.now(),
              vehicle: body.vehicle,
            },
          });
        }
        await auth.axios.post(
          `/api/v1/patrol/${encodeURIComponent(id)}/ecc-log-on`,
          body,
        );
        enqueueSnackbar('Successfully notified ECC', { variant: 'success' });
        if (isImmediate) {
          navigate(`/patrol/${id}`);
        } else {
          navigate(-1);
        }
      } catch (err: unknown) {
        if (err instanceof Error) {
          enqueueSnackbar(`Unable to log on with ECC: ${err.message}`, {
            variant: 'error',
          });
        } else {
          throw err;
        }
      }
    },
    validate: async (value) => {
      const {
        vehicle,
        vehicleColor,
        vehicleHasLivery,
        vehicleMake,
        vehicleModel,
        vehicleRego,
        ...rest
      } = value;
      const a = await (vehicle
        ? formikCheck(eccLogOnFormContract, {
            vehicleColor,
            vehicleHasLivery,
            vehicleMake,
            vehicleModel,
            vehicleRego,
            ...rest,
          })
        : formikCheck(eccLogOnFormContractNoVehicle, rest));
      console.log(a);
      return a;
    },
    validateOnBlur: true,
  });

  const handlePreviousVehicleClick = (
    vehicle: Patrol.PatrolVehicleInfo,
  ): void => {
    eccLogOnForm
      .setValues({
        ...eccLogOnForm.values,
        vehicle: true,
        vehicleColor: vehicle.color,
        vehicleHasLivery: vehicle.hasLivery,
        vehicleMake: vehicle.make,
        vehicleModel: vehicle.model,
        vehicleRego: vehicle.rego,
      })
      .then(async () => {
        await eccLogOnForm.setFieldTouched('vehicleColor');
        await eccLogOnForm.setFieldTouched('vehicleHasLivery');
        await eccLogOnForm.setFieldTouched('vehicleMake');
        await eccLogOnForm.setFieldTouched('vehicleModel');
        await eccLogOnForm.setFieldTouched('vehicleRego');
        return;
      })
      .catch((error) => {
        enqueueSnackbar('Error selecting previous vehicle', {
          variant: 'error',
        });
        Sentry.captureException(error);
      });
  };

  const handlePreviousVehicleDelete = (
    vehicle: Patrol.PatrolVehicleInfo & { fromRegister?: boolean },
  ): void => {
    // Only allow deleting recently used vehicles, not register vehicles
    if (vehicle.fromRegister) {
      return;
    }
    setPreviousVehicles({
      ...recentVehicles
        .map((recent) => previousVehicles[recent.rego])
        .reduce<Record<string, PreviousVehicles>>(
          (acc, item) =>
            item && item.vehicle.rego !== vehicle.rego
              ? { ...acc, [item.vehicle.rego]: item }
              : acc,
          {},
        ),
    });
  };

  if (patrol.finished) {
    return <AlertCard severity="error">Patrol is already finished.</AlertCard>;
  }
  if (!patrol.finalized) {
    return (
      <AlertCard severity="error">
        Patrol must be finalized to be started.
      </AlertCard>
    );
  }
  if (patrol.cancelled) {
    return <AlertCard severity="error">Patrol has been cancelled.</AlertCard>;
  }
  if (patrol.started) {
    return <AlertCard severity="error">Patrol is already started.</AlertCard>;
  }
  if (patrol.eccLogOnNotification) {
    return (
      <AlertCard severity="error">
        ECC LogOn notification already sent.
      </AlertCard>
    );
  }

  return (
    <EccLogOnView
      cancel={() => {
        if (isImmediate) {
          navigate(`/patrol/${id}`);
        } else {
          navigate(-1);
        }
      }}
      formik={eccLogOnForm}
      isImmediate={isImmediate}
      onRecentVehicleClick={handlePreviousVehicleClick}
      onRecentVehicleDelete={handlePreviousVehicleDelete}
      recentVehicles={combinedVehicles}
    />
  );
};

export default (): JSX.Element => <ExistingPatrolPage view={EccLogOnContent} />;
